import {
  Button,
  Page,
  PageBody,
  Result,
  Stack,
} from '@pypestream/design-system';
import { isIgnoredError, reportError } from '@pypestream/utils';
import { FC, useEffect } from 'react';
import {
  ErrorBoundary as ReactErrorBoundary,
  useErrorBoundary,
} from 'react-error-boundary';

import { logError } from './helpers';
import { ChildrenProps, ErrorFlags, ErrorPageLabels, ErrorTags } from './types';

/** What the listener layer needs: `labels` are only read by the fallback page. */
interface UncaughtErrorBoundaryProps extends ChildrenProps {
  /** Errors the app's state machine is currently holding. */
  errors?: Error[] | null;
  flags: ErrorFlags;
  /** Reporting is skipped in development, as it was in both apps. */
  isDevelopment: boolean;
}

interface AppErrorBoundaryProps extends UncaughtErrorBoundaryProps {
  labels: ErrorPageLabels;
}

const UncaughtErrorBoundary: FC<UncaughtErrorBoundaryProps> = ({
  children,
  errors: machineErrors,
  flags,
  isDevelopment,
}) => {
  const { isLocalError, isGlobalError, isXstateError } = flags;
  const errors = !isLocalError ? machineErrors : null;
  const { showBoundary } = useErrorBoundary();

  const handler = (e: ErrorEvent) => {
    // Benign browser notification, not a real error — it must not throw the
    // user onto an error page. Same rule Sentry filters on, read from one place.
    if (isIgnoredError(e.message ?? '')) {
      return;
    }

    showBoundary(e);
  };

  const promiseRejectionHandler = (e: PromiseRejectionEvent) => {
    const error =
      e.reason instanceof Error ? e.reason : new Error(String(e.reason));

    // The same filter the `error` path uses. Without it the noise this package
    // exists to swallow still reaches the user, just through the other entrance.
    if (isIgnoredError(error.message)) {
      return;
    }

    showBoundary(error);
  };

  const removeErrorListener = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('error', handler);
    window.removeEventListener('unhandledrejection', promiseRejectionHandler);
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isLocalError || isXstateError) {
      removeErrorListener();

      return;
    }

    window.addEventListener('error', handler);
    window.addEventListener('unhandledrejection', promiseRejectionHandler);

    return () => {
      removeErrorListener();
    };
    // `isXstateError` is read in the body above: without it here, entering an
    // xstate error does not re-run the effect and the listeners stay attached,
    // which is the opposite of what the branch says it does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalError, isXstateError]);

  useEffect(() => {
    if (
      (isGlobalError || isLocalError || isXstateError) &&
      machineErrors &&
      !isDevelopment
    ) {
      machineErrors.forEach((error) => {
        reportError(error, {
          source: 'UncaughtErrorBoundary',
          type: isGlobalError
            ? 'GlobalError'
            : isLocalError
              ? 'LocalError'
              : isXstateError
                ? 'XstateError'
                : 'UnknownError',
          tags: {
            ...(isXstateError && { [ErrorTags.xstate]: String(isXstateError) }),
            ...(isLocalError && { [ErrorTags.local]: String(isLocalError) }),
            ...(isGlobalError && { [ErrorTags.global]: String(isGlobalError) }),
          },
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGlobalError,
    isLocalError,
    isXstateError,
    machineErrors,
    isDevelopment,
  ]);

  useEffect(() => {
    if (!isGlobalError || !errors || errors.length === 0) {
      return;
    }

    showBoundary(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobalError, errors]);

  return <>{children}</>;
};

interface ErrorPageProps {
  error: Error | [Error];
  resetErrorBoundary: () => void;
  labels: ErrorPageLabels;
}

/**
 * Declared at module level, not inside the boundary: a component defined during
 * render has a fresh identity every pass, which remounts the whole fallback.
 */
const ErrorPage: FC<ErrorPageProps> = ({
  error,
  resetErrorBoundary,
  labels,
}) => (
  <Page background="secondary">
    <PageBody background="none">
      <Result
        align="center"
        status="error"
        label={Array.isArray(error) ? error[0].message : error.message}
        body={labels.body}
      >
        <Stack gutter="xsmall" justifyContent="center" slot="footer">
          <Button size="large" variant="secondary" onClick={resetErrorBoundary}>
            {labels.reload}
          </Button>
          <Button
            size="large"
            onClick={() => (document.location.pathname = '/')}
          >
            {labels.home}
          </Button>
        </Stack>
      </Result>
    </PageBody>
  </Page>
);

/**
 * The error boundary both apps render at their root.
 *
 * Everything app-specific arrives as a prop — the machine's errors, the flags
 * each app resolves from its own XState hook, and the already-translated
 * labels. That is what lets one implementation serve both: the package holds no
 * user-facing text and knows nothing about either machine.
 */
export const AppErrorBoundary: FC<AppErrorBoundaryProps> = ({
  children,
  labels,
  ...rest
}) => {
  const resetApp = () => {
    window.location.reload();
  };

  return (
    <ReactErrorBoundary
      fallbackRender={(props) => <ErrorPage {...props} labels={labels} />}
      onError={logError}
      onReset={resetApp}
    >
      <UncaughtErrorBoundary {...rest}>{children}</UncaughtErrorBoundary>
    </ReactErrorBoundary>
  );
};
